import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigationHelpers } from "../../hooks/useNavigationHelpers";
import { useNavigate } from "react-router-dom";
import { onDirectUpload } from "../../helpers";
import { createLearner } from "../../api/endpoints/learner/learner";
import { ApiResponse } from "../../api/apiResponse";
import toast from "react-hot-toast";

interface AudioPreviewProps {
  onSubmit?: (blobUrl: string) => void;
  onReRecord?: () => void;
}

const AudioPreview: React.FC<AudioPreviewProps> = ({
  onSubmit,
  onReRecord,
}) => {
  const { t } = useTranslation("", { keyPrefix: "page.readingRecorder" });
  const [audioBlobUrl, setAudioBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [apiData, setApiData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const { handleBack } = useNavigationHelpers();
  const navigate = useNavigate();

  useEffect(() => {
    // Reload the page first, then get the audio blob
    const hasReloaded = sessionStorage.getItem('audioPreviewReloaded');
    
    if (!hasReloaded) {
      // Mark as reloaded and reload the page
      sessionStorage.setItem('audioPreviewReloaded', 'true');
      window.location.reload();
      return;
    }

    // Get the recorded audio from localStorage
    const recordedData = localStorage.getItem('recordedAudioData');
    if (recordedData) {
      console.log('Found recorded audio data, length:', recordedData.length);
      
      // Convert base64 data back to blob URL
      try {
        // recordedData is in format: "data:audio/webm;base64,..."
        setAudioBlobUrl(recordedData); // Set the data URL directly for audio element
        console.log('Audio blob URL set successfully');
      } catch (error) {
        console.error('Error processing audio data:', error);
        setError(t("invalidRecording") || "Invalid recording data");
      }
    } else {
      console.log('No recorded audio data found in localStorage');
      setError(t("noRecordingFound") || "No recording found");
    }

    // Get API data for ReadingRecorder upload if available
    const storedApiData = localStorage.getItem('recordingApiData');
    if (storedApiData) {
      try {
        setApiData(JSON.parse(storedApiData));
      } catch (error) {
        console.error('Error parsing API data:', error);
      }
    }
    
    setIsLoading(false);

    // Clean up the session storage flag
    return () => {
      sessionStorage.removeItem('audioPreviewReloaded');
    };
  }, []);

  // Clean up storage when component unmounts or user navigates away
//   useEffect(() => {
//     const cleanup = () => {
//       sessionStorage.removeItem('audioPreviewReloaded');
//                 localStorage.removeItem('recordedAudioData');
//     };

//     // Clean up on page unload
//     window.addEventListener('beforeunload', cleanup);
    
//     return () => {
//       window.removeEventListener('beforeunload', cleanup);
//       // Don't cleanup here as it will be called too early
//     };
//   }, []);

  const handleSubmit = async () => {
    if (!audioBlobUrl) return;

    // If there's API data, handle ReadingRecorder upload
    if (apiData) {
      await uploadForReadingRecorder();
    } else if (onSubmit) {
      // Direct callback submission
      onSubmit(audioBlobUrl);
    }
  };

  const uploadForReadingRecorder = async () => {
    if (!audioBlobUrl) return;

    try {
      setIsUploading(true);
      
      // Convert data URL back to blob for upload
      const response = await fetch(audioBlobUrl);
      const blob = await response.blob();

      const file = new File([blob], `recording-${Date.now()}.webm`, {
        type: blob.type || 'audio/webm',
      });

      onDirectUpload(file, async (signed_id: any) => {
        const apiResponse: ApiResponse = await createLearner({
          ...apiData,
          submission: signed_id,
        });

        if (apiResponse.isSuccess) {
          toast.success(t("savedSuccessfully") || "Recording saved successfully");
          // Clean up localStorage
          localStorage.removeItem('recordedAudioData');
          localStorage.removeItem('recordingApiData');
          navigate("/learner/success-booking-account", { replace: true });
        } else {
          apiResponse.errorMessages.forEach((errorMessage) => {
            toast.error(errorMessage);
          });
        }
      });
    } catch (error) {
      console.error("Error converting blob to file:", error);
      toast.error(t("uploadError") || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleReRecord = () => {
    // Clear the stored audio and navigate back to recording
    localStorage.removeItem('recordedAudioData');
    sessionStorage.removeItem('audioPreviewReloaded');
    
    // Clear API data if it exists
    if (apiData) {
      localStorage.removeItem('recordingApiData');
    }
    
    if (onReRecord) {
      onReRecord();
    } else if (apiData) {
      // Navigate back to ReadingRecorder with the same type
      const currentPath = window.location.pathname;
      if (currentPath.includes('/learner/')) {
        navigate('/learner/reading-recorder/audio');
      } else {
        handleBack();
      }
    } else {
      // Default behavior: go back to previous page
      handleBack();
    }
  };

  const handleRetry = () => {
    setError(null);
    setIsLoading(true);
    // Clear flags and try again
    sessionStorage.removeItem('audioPreviewReloaded');
    window.location.reload();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg">{t("loading") || "Loading..."}</div>
      </div>
    );
  }

  if (!audioBlobUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-lg text-red-600">
          {t("noRecordingFound") || "No recording found"}
        </div>
        <button
          onClick={handleBack}
          className="px-4 py-2 text-white bg-blue-600 rounded"
        >
          {t("back") || "Go Back"}
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={handleBack}
          className="px-4 py-2 text-white bg-blue-600 rounded"
        >
          {t("back") || "Go Back"}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-6 p-4">
      <h1 className="text-2xl font-bold">
        {t("previewRecording") || "Preview Recording"}
      </h1>
      
      <div className="w-full max-w-2xl">
        <audio
          src={audioBlobUrl}
          controls
          className="w-full"
          preload="metadata"
          autoPlay={false}
        />
      </div>

      {isUploading && (
        <div className="flex items-center gap-2 text-blue-600">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
          <span>{t("uploading") || "Uploading..."}</span>
        </div>
      )}

      <div className="flex gap-4 justify-center w-full max-w-2xl">
        <button
          onClick={handleReRecord}
          disabled={isUploading}
          className="px-6 py-3 text-white bg-gray-500 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("recordRecord") || "Re-record"}
        </button>
        
        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className="px-6 py-3 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isUploading ? (t("uploading") || "Uploading...") : (t("submit") || "Submit")}
        </button>
      </div>
    </div>
  );
};

export default AudioPreview; 